import React, { useState } from 'react';
import { Link } from 'react-router';
import type { LscVodCandidate, LscVodMatchSuggestion } from '../types';
import {
  reviewVodCandidate,
  publishVodCandidate,
  assignVodMatch,
  unassignVodMatch,
  LscPublishConflictError,
} from '../api';
import ReviewStatusBadge from './ReviewStatusBadge';
import PublishConfirmModal from './PublishConfirmModal';
import RejectVodConfirmModal from './RejectVodConfirmModal';
import UnassignMatchConfirmModal from './UnassignMatchConfirmModal';
import LscMutationErrorAlert from './LscMutationErrorAlert';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';
import { lscPath } from '../routing';
import {
  TRUTH_REVIEW_LOCKED_COPY,
  truthAssignBlocked,
  truthCrossLeagueConflictMessage,
  truthPublishBlocked,
  truthPublishBlockedMessage,
  truthReviewActionsLocked,
  truthRecommendedActionGuidance,
  truthScheduleBindingHeadline,
  truthIsOverrideEligible,
  truthIsEmptyHudHandoffAttestationEligible,
  truthLeagueMatchResolution,
  truthOverridableIdentityTags,
  truthCanRejectWithoutMatchSuggestion,
  NO_LEAGUE_MATCH_REVIEW_GUIDANCE,
  formatSuggestionOptionLabel,
  formatSuggestionOptionTitle,
  truthPublishBlockedSeverity,
  canApproveHandoffLinkedCandidate,
} from '../vodTruth';
import {
  DISMISS_HELPER_COPY,
  REVIEW_ACTIONS_HANDOFF_CTA,
  REVIEW_ACTIONS_RESOLVE_IDENTITY_CTA,
} from '../commissionerReviewCopy';

interface ReviewActionPanelProps {
  leagueId: string;
  candidate: LscVodCandidate;
  suggestions: LscVodMatchSuggestion[];
  onUpdated: () => Promise<void>;
  isCommissioner?: boolean;
}

const ReviewActionPanel: React.FC<ReviewActionPanelProps> = ({
  leagueId,
  candidate,
  suggestions,
  onUpdated,
  isCommissioner = true,
}) => {
  const [note, setNote] = useState('');
  const [selectedSuggestionId, setSelectedSuggestionId] = useState(
    suggestions[0]?.id ?? ''
  );
  const [actionInFlight, setActionInFlight] = useState(false);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [unassignSuccess, setUnassignSuccess] = useState(false);
  const [rejectSuccess, setRejectSuccess] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState<{
    archiveId: string;
    idempotent?: boolean;
  } | null>(null);
  const [conflictArchiveId, setConflictArchiveId] = useState<string | null>(null);

  const reviewStatus = candidate.review_status ?? 'pending_review';
  const assignedMatchId = candidate.competition_match_id ?? null;
  const bestSuggestionId = suggestions[0]?.id;
  const recommendedGuidance = truthRecommendedActionGuidance(candidate);
  const publishView = candidate.commissioner_publish;
  const scheduleBindingHeadline = truthScheduleBindingHeadline(candidate);
  const reviewLocked = truthReviewActionsLocked(candidate);
  const publishBlocked = truthPublishBlocked(candidate);
  const assignBlocked = truthAssignBlocked(candidate);
  const crossLeagueMessage = truthCrossLeagueConflictMessage(candidate);
  const publishBlockedMessage = truthPublishBlockedMessage(candidate);
  const publishBlockedSeverity = truthPublishBlockedSeverity(candidate);
  const reviewActionsBlocked = reviewLocked || assignBlocked;
  const canUnassignMatch =
    isCommissioner &&
    Boolean(assignedMatchId) &&
    reviewStatus !== 'published' &&
    !reviewLocked;
  const hasMatchSuggestions = suggestions.length > 0;
  const canApproveHandoffMatch = canApproveHandoffLinkedCandidate(candidate);
  const canApproveMatch =
    (hasMatchSuggestions && Boolean(selectedSuggestionId) && !reviewActionsBlocked) ||
    (canApproveHandoffMatch && !reviewActionsBlocked);
  const canRejectWithoutSuggestion = truthCanRejectWithoutMatchSuggestion(
    candidate,
    suggestions
  );
  const showNoLeagueMatchGuidance =
    !publishView &&
    !reviewLocked &&
    !hasMatchSuggestions &&
    (truthLeagueMatchResolution(candidate) === 'no_league_match' ||
      !candidate.competition_match_id);
  const overrideEligible = truthIsOverrideEligible(candidate);
  const identityOverrideNeeded =
    publishBlocked &&
    truthLeagueMatchResolution(candidate) === 'possible_team_match' &&
    overrideEligible &&
    truthOverridableIdentityTags(candidate).length > 0;
  const emptyHudAttestationNeeded =
    publishBlocked &&
    truthIsEmptyHudHandoffAttestationEligible(candidate);

  const runReview = async (action: 'approve' | 'reject' | 'dismiss') => {
    setActionInFlight(true);
    setError(null);
    setConflictArchiveId(null);
    setRejectSuccess(false);
    try {
      const payload: {
        action: 'approve' | 'reject' | 'dismiss';
        note?: string;
        suggestion_id?: string;
      } = { action, note: note || undefined };
      if (action === 'approve' && selectedSuggestionId) {
        payload.suggestion_id = selectedSuggestionId;
      }
      await reviewVodCandidate(leagueId, candidate.id, payload);
      if (action === 'reject') {
        setRejectSuccess(true);
      }
      await onUpdated();
    } catch (err) {
      setError(parseLscMutationError(err, 'Review action failed.'));
    } finally {
      setActionInFlight(false);
    }
  };

  const runAssignMatch = async (suggestionId: string) => {
    setActionInFlight(true);
    setError(null);
    try {
      await assignVodMatch(leagueId, candidate.id, {
        suggestion_id: suggestionId,
        note: note || undefined,
      });
      await onUpdated();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not assign match.'));
    } finally {
      setActionInFlight(false);
    }
  };

  const handleUnassign = async (unassignNote: string) => {
    setUnassigning(true);
    setError(null);
    setUnassignSuccess(false);
    try {
      await unassignVodMatch(leagueId, candidate.id, {
        note: unassignNote || note || undefined,
      });
      setUnassignOpen(false);
      setUnassignSuccess(true);
      await onUpdated();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not unassign match.'));
    } finally {
      setUnassigning(false);
    }
  };

  const handlePublish = async (visibility: string, publishNote: string) => {
    setPublishing(true);
    setError(null);
    setConflictArchiveId(null);
    try {
      const result = await publishVodCandidate(leagueId, candidate.id, {
        visibility,
        note: publishNote || undefined,
        suggestion_id: selectedSuggestionId || bestSuggestionId,
      });
      const archiveId = result.archive?.id ?? candidate.archive_id;
      if (archiveId) {
        setPublishSuccess({ archiveId, idempotent: result.idempotent });
      }
      setPublishOpen(false);
      await onUpdated();
    } catch (err) {
      if (err instanceof LscPublishConflictError) {
        setConflictArchiveId(err.archiveId ?? null);
        setError({ message: err.message, showBillingLink: false, showPricingLink: false });
        setPublishOpen(false);
      } else {
        setError(parseLscMutationError(err, 'Publish failed.'));
      }
    } finally {
      setPublishing(false);
    }
  };

  const archiveId = publishSuccess?.archiveId ?? candidate.archive_id;

  return (
    <div className="card space-y-4" id="review-actions" data-testid="review-action-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-white">Review actions</h2>
        <ReviewStatusBadge status={reviewStatus} />
      </div>

      <LscMutationErrorAlert error={error} onDismiss={() => setError(null)} />

      {reviewLocked && (
        <div
          className="rounded-lg border border-secondary-600 bg-secondary-800/60 p-4 text-sm text-secondary-300"
          data-testid="review-actions-locked"
        >
          <p className="font-medium text-secondary-200 mb-1">Review actions locked</p>
          <p>{TRUTH_REVIEW_LOCKED_COPY}</p>
        </div>
      )}

      {crossLeagueMessage && !reviewLocked && !publishView && (
        <div
          className="rounded-lg border border-red-800 bg-red-900/30 p-4 text-sm"
          data-testid="cross-league-conflict-warning"
        >
          <p className="font-medium text-red-100 mb-1">Possible wrong league</p>
          <p className="text-red-200">{crossLeagueMessage}</p>
        </div>
      )}

      {publishBlockedMessage && !reviewLocked && !publishView?.teams_match && (
        <p
          className={`text-sm border-l-2 pl-3 ${
            publishBlockedSeverity === 'danger'
              ? 'text-red-200 border-red-700'
              : 'text-amber-200 border-amber-600'
          }`}
          data-testid="publish-blocked-message"
        >
          {publishBlockedMessage}
        </p>
      )}

      {identityOverrideNeeded && !reviewLocked && (
        <p className="text-sm text-amber-200/90" data-testid="identity-override-cta">
          <a href="#identity-override" className="text-primary-300 hover:text-primary-200">
            {REVIEW_ACTIONS_RESOLVE_IDENTITY_CTA}
          </a>
        </p>
      )}

      {emptyHudAttestationNeeded && !reviewLocked && (
        <p className="text-sm text-amber-200/90" data-testid="empty-hud-handoff-attestation-cta">
          <a
            href="#empty-hud-handoff-attestation"
            className="text-primary-300 hover:text-primary-200"
          >
            {REVIEW_ACTIONS_HANDOFF_CTA}
          </a>
        </p>
      )}

      {scheduleBindingHeadline && !reviewLocked && !assignBlocked && !publishView && (
        <p
          className="text-sm text-secondary-300 border-l-2 border-green-700 pl-3"
          data-testid="review-schedule-binding-headline"
        >
          {scheduleBindingHeadline}
        </p>
      )}

      {recommendedGuidance && !reviewLocked && !publishView && (
        <p
          className="text-sm text-secondary-300 border-l-2 border-primary-600 pl-3"
          data-testid="review-recommended-action"
        >
          {recommendedGuidance}
        </p>
      )}

      {showNoLeagueMatchGuidance && (
        <p
          className="text-sm text-secondary-300 border-l-2 border-amber-600 pl-3"
          data-testid="no-league-match-review-guidance"
        >
          {NO_LEAGUE_MATCH_REVIEW_GUIDANCE}
        </p>
      )}

      {rejectSuccess && (
        <div
          className="rounded-lg border border-green-700 bg-green-900/30 p-3 text-sm"
          data-testid="reject-success-banner"
        >
          <p className="text-green-100">VOD rejected and removed from review queue.</p>
        </div>
      )}

      {conflictArchiveId && (
        <div className="rounded-lg border border-amber-700 bg-amber-900/30 p-3 text-sm">
          <p className="text-amber-100 mb-2">
            This VOD is already published to another match.
          </p>
          <Link
            to={lscPath(
              `/leagues/${leagueId}/archive/${encodeURIComponent(conflictArchiveId)}`
            )}
            className="text-primary-300 hover:text-primary-200"
          >
            View existing archive entry
          </Link>
        </div>
      )}

      {unassignSuccess && (
        <div
          className="rounded-lg border border-green-700 bg-green-900/30 p-3 text-sm"
          data-testid="unassign-match-success-banner"
        >
          <p className="text-green-100">
            Match assignment removed. Select the correct schedule match before publishing.
          </p>
        </div>
      )}

      {publishSuccess && archiveId && (
        <div className="rounded-lg border border-green-700 bg-green-900/30 p-3 text-sm">
          <p className="text-green-100 mb-2">
            {publishSuccess.idempotent
              ? 'Already published to the archive.'
              : 'Published to the league archive.'}
          </p>
          <Link
            to={lscPath(`/leagues/${leagueId}/archive/${encodeURIComponent(archiveId)}`)}
            className="text-primary-300 hover:text-primary-200"
          >
            View archive entry
          </Link>
        </div>
      )}

      {reviewStatus === 'published' && archiveId && !publishSuccess && (
        <div className="rounded-lg border border-primary-700 bg-primary-900/30 p-3 text-sm">
          <p className="text-primary-100 mb-2">This VOD is in the league archive.</p>
          <Link
            to={lscPath(`/leagues/${leagueId}/archive/${encodeURIComponent(archiveId)}`)}
            className="text-primary-300 hover:text-primary-200"
          >
            View archive entry
          </Link>
        </div>
      )}

      {reviewStatus === 'pending_review' && (
        <>
          {suggestions.length > 0 && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary-400">Match to approve</span>
              <select
                className="input-field"
                value={selectedSuggestionId}
                onChange={(e) => setSelectedSuggestionId(e.target.value)}
                disabled={actionInFlight}
              >
                {suggestions.map((s) => (
                  <option
                    key={s.id}
                    value={s.id}
                    title={formatSuggestionOptionTitle(s) ?? undefined}
                  >
                    {formatSuggestionOptionLabel(s)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Note (optional)</span>
            <textarea
              className="input-field min-h-[60px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={actionInFlight}
              placeholder="Optional review note"
            />
          </label>
          <div className="flex flex-wrap gap-3">
            {(hasMatchSuggestions || canApproveHandoffMatch) && (
              <button
                type="button"
                className="btn-primary"
                disabled={actionInFlight || !canApproveMatch}
                onClick={() => runReview('approve')}
                data-testid="approve-match-button"
              >
                {actionInFlight ? 'Working...' : 'Approve match'}
              </button>
            )}
            <button
              type="button"
              className="btn-secondary"
              disabled={
                actionInFlight ||
                reviewLocked ||
                (!hasMatchSuggestions && !canRejectWithoutSuggestion)
              }
              onClick={() => setRejectOpen(true)}
              data-testid="reject-match-button"
            >
              Reject
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={
                actionInFlight ||
                reviewLocked ||
                (!hasMatchSuggestions && !canRejectWithoutSuggestion)
              }
              onClick={() => runReview('dismiss')}
              data-testid="dismiss-match-button"
            >
              {hasMatchSuggestions ? 'Dismiss suggestion' : 'Dismiss candidate'}
            </button>
          </div>
          <p className="text-xs text-secondary-500" data-testid="dismiss-helper-copy">
            {DISMISS_HELPER_COPY}
          </p>
        </>
      )}

      {(reviewStatus === 'approved' ||
        (publishView?.teams_match &&
          reviewStatus !== 'published' &&
          reviewStatus !== 'rejected' &&
          reviewStatus !== 'dismissed')) && (
        <div className="space-y-3">
          {reviewStatus === 'approved' && (
            <p className="text-sm text-secondary-400">
              Match approved. Publish separately when ready — publishing does not happen
              automatically.
            </p>
          )}
          {canUnassignMatch && (
            <button
              type="button"
              className="btn-secondary text-red-300 border-red-800 hover:bg-red-900/30"
              disabled={actionInFlight || unassigning}
              onClick={() => setUnassignOpen(true)}
              data-testid="unassign-match-button"
            >
              Unassign match
            </button>
          )}
          <button
            type="button"
            className={`btn-primary${publishBlocked ? ' opacity-50 cursor-not-allowed' : ''}`}
            disabled={actionInFlight || publishing || reviewLocked || publishBlocked}
            onClick={() => setPublishOpen(true)}
            title={
              publishBlocked && publishBlockedMessage
                ? publishBlockedMessage
                : 'Publish this approved VOD to the league archive'
            }
            data-testid="publish-to-archive-button"
          >
            {publishing ? 'Publishing…' : 'Publish to archive'}
          </button>
        </div>
      )}

      {reviewStatus === 'pending_review' && canUnassignMatch && (
        <button
          type="button"
          className="btn-secondary text-red-300 border-red-800 hover:bg-red-900/30"
          disabled={actionInFlight || unassigning}
          onClick={() => setUnassignOpen(true)}
          data-testid="unassign-match-button"
        >
          Unassign match
        </button>
      )}

      {reviewStatus === 'dismissed' && (
        <div className="space-y-3" data-testid="dismissed-candidate-copy">
          <p className="text-sm text-secondary-400">
            This candidate was dismissed. It is out of the review queue.
          </p>
        </div>
      )}

      {reviewStatus === 'rejected' && (
        <div className="space-y-3">
          <p className="text-sm text-secondary-400">
          This candidate was rejected. Re-run match from title & stream info after updating schedule
            or VOD metadata, then review again.
          </p>
        </div>
      )}

      {suggestions.length > 0 && reviewStatus !== 'published' && !reviewActionsBlocked && (
        <div className="border-t border-secondary-700 pt-4">
          <p className="text-sm text-secondary-500 mb-1">Assign a different match</p>
          <p
            className="text-xs text-secondary-500 mb-2"
            data-testid="review-single-assignment-note"
          >
            Only one match can be assigned. Selecting another match replaces the current
            assignment.
          </p>
          <div className="flex flex-wrap gap-2">
            {suggestions.slice(0, 5).map((s) => (
              <button
                key={s.id}
                type="button"
                className="btn-secondary text-xs"
                disabled={actionInFlight}
                onClick={() => runAssignMatch(s.id)}
                title={formatSuggestionOptionTitle(s) ?? undefined}
              >
                Use {formatSuggestionOptionLabel(s)}
              </button>
            ))}
          </div>
        </div>
      )}

      <RejectVodConfirmModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={async () => {
          await runReview('reject');
          setRejectOpen(false);
        }}
        rejecting={actionInFlight}
      />

      <PublishConfirmModal
        open={publishOpen}
        title={candidate.title}
        confirm={
          candidate.commissioner_publish?.confirm
            ? {
                ...candidate.commissioner_publish.confirm,
                gamertag_warning:
                  candidate.commissioner_publish.confirm.gamertag_warning ||
                  candidate.commissioner_publish.warning,
              }
            : candidate.commissioner_publish?.warning
              ? { gamertag_warning: candidate.commissioner_publish.warning }
              : null
        }
        onClose={() => setPublishOpen(false)}
        onConfirm={handlePublish}
        publishing={publishing}
      />

      <UnassignMatchConfirmModal
        open={unassignOpen}
        onClose={() => setUnassignOpen(false)}
        onConfirm={handleUnassign}
        unassigning={unassigning}
      />
    </div>
  );
};

export default ReviewActionPanel;
